/**
 * Lire une étiquette QR depuis une photo, et en tirer le code de l'objet.
 *
 * *Extrait le 15/09/2026, quand l'inventaire est devenu le troisième écran à scanner.* Le même
 * décodeur vivait déjà en deux exemplaires — « Saisir un OR » et « Stocks » —, et ils avaient
 * commencé à diverger : un commentaire reformulé aujourd'hui, un correctif appliqué à un seul
 * demain. Trois copies d'un lecteur d'étiquettes, c'est trois comportements devant le même
 * rayonnage.
 *
 * ## Pourquoi deux passes de décodage
 *
 * Une photo de téléphone fait 4 000 px de large, et jsQR y travaillerait sur douze millions de
 * pixels pour un QR qui en occupe quelques centaines. La réduction est donc tentée d'abord. Mais
 * un QR **petit dans une grande photo** — une étiquette prise de loin — disparaît à la réduction :
 * la pleine résolution est reprise en second, et seulement si la première a échoué.
 */
import jsQR from "jsqr";

/** Au-delà de cette largeur, la photo est réduite avant décodage. */
const LARGEUR_DECODAGE = 1400;

/** Rend le texte encodé par le QR de cette image, ou `null` si aucun n'est lisible. */
export async function lireQrDansImage(fichier: File): Promise<string | null> {
  // `from-image` respecte l'orientation EXIF : les photos d'iPhone arrivent pivotées, et jsQR
  // sait lire un QR tourné mais pas une image dont les dimensions ne correspondent pas aux
  // pixels qu'on lui donne.
  const image = await createImageBitmap(fichier, { imageOrientation: "from-image" });

  const essayer = (largeur: number): string | null => {
    const echelle = Math.min(1, largeur / image.width);
    const l = Math.max(1, Math.round(image.width * echelle));
    const h = Math.max(1, Math.round(image.height * echelle));
    const toile = document.createElement("canvas");
    toile.width = l;
    toile.height = h;
    const ctx = toile.getContext("2d", { willReadFrequently: true });
    if (ctx === null) return null;
    ctx.drawImage(image, 0, 0, l, h);
    const pixels = ctx.getImageData(0, 0, l, h);
    const trouve = jsQR(pixels.data, l, h, { inversionAttempts: "attemptBoth" });
    return trouve === null ? null : trouve.data;
  };

  const reduit = essayer(LARGEUR_DECODAGE);
  if (reduit !== null) {
    image.close();
    return reduit;
  }
  const entier = image.width > LARGEUR_DECODAGE ? essayer(image.width) : null;
  image.close();
  return entier;
}

/**
 * Le code article contenu dans ce que le QR a rendu.
 *
 * Le QR encode une ADRESSE — `https://<domaine>/article/<code>` — depuis le 20/08/2026, pour
 * qu'un appareil photo de téléphone ouvre quelque chose. On accepte quand même un code nu : une
 * étiquette imprimée avant ce changement, ou un lecteur qui ne rendrait que la charge utile, ne
 * doivent pas rester lettre morte devant le magasinier.
 */
export function codeDuScan(scan: string): string | null {
  const texte = scan.trim();
  if (texte === "") return null;
  const parAdresse = /\/article\/([^/?#]+)/.exec(texte);
  if (parAdresse?.[1] !== undefined) return decodeURIComponent(parAdresse[1]);
  // Pas une adresse : ce doit être le code lui-même. Une chaîne qui contient une barre oblique
  // sans être une adresse d'article n'est ni l'un ni l'autre, et on préfère le dire.
  return texte.includes("/") ? null : texte;
}
